import React, { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'

import { RootState } from '../../../admin/redux/store'
import { useAdaptConnectorNaming } from '../../../lib/adaptConnectorNaming'
import isAura from '../../../lib/isAura'
import Text from '../../../ui/Text'
import { ChargePoint, useCompanyGetPoolsQuery, useCompanyGetUsersQuery } from '../../redux/api/web-api'
import { SFormRow } from '../../styles/Form'
import useText from '../../hooks/useText'
import { Input } from '../../uikit/Input'

import { SConnectorWrapper } from './chargepoint-details-widget.styles'

interface ComponentProps {
    chargePoint: ChargePoint
}

const ChargePointDetailsAdminInputs = ({ chargePoint }: ComponentProps) => {
    const text = useText()
    const selectedCompanyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const adaptConnectorNaming = useAdaptConnectorNaming()

    const [connectorRegistration, setConnectorRegistration] = useState(
        chargePoint.connectors?.some((connector) => !!connector.connectorUserId)
    )

    const {
        register,
        control,
        formState: { errors },
        setValue,
    } = useFormContext()

    const { data: companyPools } = useCompanyGetPoolsQuery({
        companyId: selectedCompanyId,
    })
    const { data: companyUsers } = useCompanyGetUsersQuery({
        companyId: selectedCompanyId,
    })

    const getOwnerInfoLabel = (connectorId: number) => {
        if (connectorId > 0 && isAura(chargePoint)) {
            return text('lblConnectorOwnerField', {
                connectorName: text(connectorId === 1 ? 'lblLeftSocket' : 'lblRightSocket'),
            })
        }

        return text('lblChargerOwnerField')
    }

    const toggleConnectorRegistration = () => {
        if (connectorRegistration) {
            chargePoint.connectors?.map((connector) => {
                setValue(`ownerInfo_${connector.connectorId}`, undefined, { shouldDirty: true, shouldValidate: true })
            })
        } else {
            setValue('ownerInfo_0', undefined, { shouldDirty: true, shouldValidate: true })
        }

        setConnectorRegistration(!connectorRegistration)
    }

    const registeredUser = (connectorId: number) => {
        const ownerInfo = `ownerInfo_${connectorId}`

        return (
            <SFormRow key={`registered-user-${connectorId}`}>
                <label htmlFor="ownerInfo" data-testid="ownerInfo">
                    {getOwnerInfoLabel(connectorId)}
                </label>
                <Controller
                    name={ownerInfo}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            id={ownerInfo}
                            data-testid={`ownerInfo-input-${connectorId}`}
                            filter
                            options={companyUsers?.map((user) => ({
                                value: user.id,
                                label: user.fullName,
                            }))}
                            showClear
                            onChange={(event) => {
                                onChange(event.value || null)
                            }}
                            value={value}
                            className="w-full"
                        />
                    )}
                />
            </SFormRow>
        )
    }

    const externalId = (connectorName: string, connectorId: number) => {
        const externalId = `externalId_${connectorId}`

        return (
            <div key={`connector-${connectorId}`} className="w-full">
                <label htmlFor={externalId} data-testid={`${externalId}-label`}>
                    {connectorName}
                </label>
                <Input
                    type="text"
                    id={externalId}
                    data-testid={externalId}
                    {...register(externalId)}
                    error={errors[externalId]?.message}
                />
            </div>
        )
    }

    return (
        <>
            <SFormRow>
                <Input
                    type="text"
                    label={<Text code="lblDescriptionField" />}
                    id="description"
                    data-testid="description-input"
                    {...register('description')}
                    error={errors.description?.message}
                />
            </SFormRow>
            <SFormRow>
                <label>
                    <Text code="lblExternalIdForReportsField" />
                </label>
                <SConnectorWrapper>
                    {chargePoint.connectors?.map((connector, index) =>
                        externalId(adaptConnectorNaming(connector.name), connector.connectorId ?? index)
                    )}
                </SConnectorWrapper>
            </SFormRow>
            <SFormRow>
                <label htmlFor="pool" data-testid="pool">
                    <Text code="lblPoolField" />
                </label>

                <Controller
                    name="pool"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <Dropdown
                            id="pool"
                            data-testid="pool-input"
                            className="w-full"
                            options={companyPools?.map((pool) => ({
                                value: pool.id,
                                label: pool.name,
                            }))}
                            showClear
                            filter
                            onChange={(event) => {
                                onChange(event.value || null)
                            }}
                            value={value}
                        />
                    )}
                />
            </SFormRow>
            {isAura(chargePoint) && (
                <SFormRow>
                    <label htmlFor="connectorRegistration" className="checkbox-form">
                        <Checkbox
                            className="mr-2"
                            id="connectorRegistration"
                            data-testid="connectorRegistration-input"
                            checked={connectorRegistration}
                            onClick={() => toggleConnectorRegistration()}
                        />
                        <span data-testid="allow-connect">
                            <Text code="lblSocketRegistrationField" />
                        </span>
                    </label>
                </SFormRow>
            )}
            {connectorRegistration
                ? chargePoint.connectors?.map((connector) => registeredUser(connector.connectorId))
                : registeredUser(0)}
        </>
    )
}

export default ChargePointDetailsAdminInputs
